import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

export interface State {
  screensaversData: OptionScreensaver[] | null;
  optionScreensaverData: object | null;
  optionBrightnessData: object | null;
  isLoading: boolean;
  error: unknown;
}

type OptionScreensaver = {
  uri: string;
  id: string;
  title: string;
};

export type LedringSettingsStoreState = State;

class LedringSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('ledring-settings');
  }

  override createInitialState() {
    return {
      screensaversData: null,
      optionScreensaverData: null,
      optionBrightnessData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const screensaversPromise = api.ledring.getScreensavers();
      const optionScreensaverPromise = api.ledring.getOptionScreensaver();
      const optionBrightnessPromise = api.ledring
        .getOptionBrightness?.()
        .catch((error: unknown) => {
          // Only exists on 201x models.
          if (
            typeof error === 'object' &&
            error !== null &&
            'code' in error &&
            error.code === 404
          ) {
            return null;
          }

          throw error;
        });

      const [screensaversData, optionScreensaverData, optionBrightnessData] = await Promise.all([
        screensaversPromise,
        optionScreensaverPromise,
        optionBrightnessPromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      const sortedScreensaversData = (screensaversData as unknown as OptionScreensaver[]).sort(
        (a, b) => {
          if (a.id === 'off') return 1;
          if (b.id === 'off') return -1;
          return 0;
        },
      );

      this.set({
        ...this.createInitialState(),
        screensaversData: sortedScreensaversData,
        optionScreensaverData: optionScreensaverData as unknown as object,
        optionBrightnessData: optionBrightnessData as unknown as object,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'ledring-settings');

    return { ledringSettings: this.store(selectAll, shallow) };
  }

  async setOptionScreensaver({ value }: { value: OptionScreensaver }) {
    const api = this.getApi();
    const result = (await api.ledring.setOptionScreensaver({ value })) as unknown as {
      value: OptionScreensaver;
    };
    this.set({ optionScreensaverData: result });
  }

  async setOptionBrightness({ value }: { value: number }) {
    const api = this.getApi();
    const result = (await api.ledring.setOptionBrightness({ value })) as unknown as {
      value: number;
    };
    this.set({ optionBrightnessData: result });
  }
}

function selectAll(state: LedringSettingsStoreState) {
  return {
    ...selectBase(state),
    screensaversData: state.screensaversData,
    optionScreensaverData: state.optionScreensaverData,
    optionBrightnessData: state.optionBrightnessData,
  };
}

export const ledringSettingsStore = new LedringSettingsStore();
